import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Pagination,
  PaginationItem,
  MenuItem,
  Select as MuiSelect,
  Typography,
  Popover,
  IconButton,
  Checkbox,
  FormControlLabel,
  Chip,
  Badge,
  TextField,
  Button,
  Divider,
  Stack,
  Tooltip,
} from "@mui/material";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import FilterAltOffRoundedIcon from "@mui/icons-material/FilterAltOffRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";

/**
 * Reusable server-side DataGrid wrapper with full sorting, numbered pagination,
 * and column-level faceted filtering with counts for all columns.
 */
export default function DataTable({
  columns,
  fetcher,
  onRowEdit,
  density = "standard",
  toolbar,
  checkboxSelection = true,
  onSelectionChange,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  instanceKey,
  extraKey,
  initialSortModel,
}) {
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = useState(initialSortModel || [{ field: "id", sort: "asc" }]);
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [selection, setSelection] = useState([]);
  const searchRef = useRef("");
  const reqSeq = useRef(0);

  // Column filtering state
  const [columnFilters, setColumnFilters] = useState({}); // { [field]: [selectedValues] }
  const [dateFilters, setDateFilters] = useState({}); // { [field]: { from, to } }
  const [activeFilterCol, setActiveFilterCol] = useState(null); // column object
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filterSearchQuery, setFilterSearchQuery] = useState("");

  useEffect(() => {
    searchRef.current = search;
  }, [search]);

  // Debounce user typing before applying search
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchText), 300);
    return () => clearTimeout(t);
  }, [searchText]);

  const load = useCallback(async () => {
    const mySeq = (reqSeq.current += 1);
    const ordering = sortModel[0]
      ? `${sortModel[0].sort === "desc" ? "-" : ""}${sortModel[0].field}`
      : "id";
    setLoading(true);
    try {
      const { results, count } = await fetcher({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: searchRef.current,
        ordering,
      });
      // Ensure each row has an id field for DataGrid
      const normalized = (results || []).map((r) => {
        const fallbackId = r.id ?? r.pk ?? r.uuid ?? r._id ?? r.key ?? `${Math.random()}`;
        const fieldsObj = r && typeof r === "object" && r.fields && typeof r.fields === "object" ? r.fields : {};
        const dataObj = r && typeof r === "object" && r.data && typeof r.data === "object" ? r.data : {};
        return { ...fieldsObj, ...dataObj, ...r, id: fallbackId };
      });
      if (mySeq === reqSeq.current) {
        const baseIndex = (paginationModel.page || 0) * (paginationModel.pageSize || 0);
        const withIndex = normalized.map((row, i) => ({
          ...row,
          __slno: baseIndex + i + 1,
          __slnoPage: i + 1,
        }));
        setRows(withIndex);
        setRowCount(count || 0);
      }
    } catch (e) {
      // no-op
    } finally {
      if (mySeq === reqSeq.current) {
        setLoading(false);
      }
    }
  }, [fetcher, paginationModel, sortModel, search, extraKey]);

  useEffect(() => {
    setPaginationModel((m) => (m.page !== 0 ? { ...m, page: 0 } : m));
  }, [search]);

  useEffect(() => {
    load();
  }, [load, instanceKey]);

  // Deep field getter
  const deepGet = (obj, path) => {
    if (!obj || !path) return undefined;
    const parts = String(path).replace(/\[(\d+)\]/g, ".$1").split(".");
    let cur = obj;
    for (const p of parts) {
      if (cur == null) return undefined;
      cur = cur[p];
    }
    return cur;
  };

  const getFieldValue = useCallback((row, field) => {
    if (!row) return "";
    if (field === "__str__") return row.repr ?? row.__str__ ?? row.name ?? "";
    if (field === "id") return row.id ?? row.pk ?? row.uuid ?? row._id ?? row.key ?? "";
    let v = deepGet(row, field);
    if (v == null) v = deepGet(row.fields, field);
    if (v == null) v = deepGet(row.data, field);
    if (v == null) return "";
    if (typeof v === "object") return v.username || v.name || v.id || String(v);
    return v;
  }, []);

  const getDisplayValue = useCallback((row, field) => {
    const val = getFieldValue(row, field);
    if (val === true) return "Yes";
    if (val === false) return "No";
    if (val === null || val === undefined || String(val).trim() === "") return "(Blank)";
    return String(val).trim();
  }, [getFieldValue]);

  const isDateField = useCallback((field, headerName = "") => {
    const f = String(field || "").toLowerCase();
    const h = String(headerName || "").toLowerCase();
    return (
      f.includes("date") ||
      f.includes("joined") ||
      f.includes("created") ||
      f.includes("buy_date") ||
      h.includes("date") ||
      h.includes("joined")
    );
  }, []);

  // Filter Popover handlers
  const handleOpenFilter = (e, col) => {
    e.stopPropagation();
    setActiveFilterCol(col);
    setFilterAnchorEl(e.currentTarget);
    setFilterSearchQuery("");
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
    setActiveFilterCol(null);
    setFilterSearchQuery("");
  };

  // Distinct options with exact counts for activeFilterCol
  const colDistinctOptions = useMemo(() => {
    if (!activeFilterCol) return [];
    const field = activeFilterCol.field;
    const countsMap = new Map();

    rows.forEach((row) => {
      const displayVal = getDisplayValue(row, field);
      countsMap.set(displayVal, (countsMap.get(displayVal) || 0) + 1);
    });

    const list = Array.from(countsMap.entries()).map(([value, count]) => ({
      value,
      count,
    }));

    list.sort((a, b) => {
      if (a.value === "(Blank)") return 1;
      if (b.value === "(Blank)") return -1;
      return b.count - a.count || String(a.value).localeCompare(String(b.value));
    });

    if (!filterSearchQuery.trim()) return list;
    const q = filterSearchQuery.toLowerCase();
    return list.filter((item) => String(item.value).toLowerCase().includes(q));
  }, [activeFilterCol, rows, getDisplayValue, filterSearchQuery]);

  // Handle toggling option in columnFilters
  const handleToggleOption = (field, optionVal) => {
    setColumnFilters((prev) => {
      const currentList = prev[field] || [];
      const exists = currentList.includes(optionVal);
      const nextList = exists
        ? currentList.filter((v) => v !== optionVal)
        : [...currentList, optionVal];

      const nextState = { ...prev };
      if (nextList.length === 0) {
        delete nextState[field];
      } else {
        nextState[field] = nextList;
      }
      return nextState;
    });
  };

  const handleSelectAllOptions = (field) => {
    const allVals = colDistinctOptions.map((o) => o.value);
    setColumnFilters((prev) => ({
      ...prev,
      [field]: allVals,
    }));
  };

  const handleClearColFilter = (field) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setDateFilters((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleClearAllFilters = () => {
    setColumnFilters({});
    setDateFilters({});
  };

  // Date filter change handler
  const handleDateFilterChange = (field, part, val) => {
    setDateFilters((prev) => {
      const curr = prev[field] || { from: "", to: "" };
      const nextRange = { ...curr, [part]: val };
      if (!nextRange.from && !nextRange.to) {
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return { ...prev, [field]: nextRange };
    });
  };

  const handleDatePreset = (field, preset) => {
    const today = new Date();
    const yyyyMmDd = (d) => d.toISOString().split("T")[0];
    let from = "";
    let to = yyyyMmDd(today);

    if (preset === "TODAY") {
      from = to;
    } else if (preset === "YESTERDAY") {
      const y = new Date();
      y.setDate(y.getDate() - 1);
      from = yyyyMmDd(y);
      to = from;
    } else if (preset === "LAST_7") {
      const d7 = new Date();
      d7.setDate(d7.getDate() - 7);
      from = yyyyMmDd(d7);
    } else if (preset === "LAST_30") {
      const d30 = new Date();
      d30.setDate(d30.getDate() - 30);
      from = yyyyMmDd(d30);
    } else if (preset === "THIS_MONTH") {
      const m1 = new Date(today.getFullYear(), today.getMonth(), 1);
      from = yyyyMmDd(m1);
    } else if (preset === "ALL") {
      from = "";
      to = "";
    }

    if (!from && !to) {
      setDateFilters((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    } else {
      setDateFilters((prev) => ({
        ...prev,
        [field]: { from, to },
      }));
    }
  };

  // Compute filtered rows
  const filteredRows = useMemo(() => {
    let result = rows;

    // 1. Column options filter
    Object.entries(columnFilters).forEach(([field, selectedVals]) => {
      if (!selectedVals || selectedVals.length === 0) return;
      const set = new Set(selectedVals);
      result = result.filter((row) => {
        const displayVal = getDisplayValue(row, field);
        return set.has(displayVal);
      });
    });

    // 2. Date filters
    Object.entries(dateFilters).forEach(([field, range]) => {
      if (!range || (!range.from && !range.to)) return;
      const fromTs = range.from ? new Date(`${range.from}T00:00:00`).getTime() : null;
      const toTs = range.to ? new Date(`${range.to}T23:59:59`).getTime() : null;

      result = result.filter((row) => {
        const raw = getFieldValue(row, field);
        if (!raw) return false;
        const d = new Date(raw).getTime();
        if (isNaN(d)) return false;
        if (fromTs && d < fromTs) return false;
        if (toTs && d > toTs) return false;
        return true;
      });
    });

    return result;
  }, [rows, columnFilters, dateFilters, getDisplayValue, getFieldValue]);

  // Total active filter count
  const activeFiltersCount = useMemo(() => {
    let c = 0;
    Object.values(columnFilters).forEach((list) => {
      if (list && list.length > 0) c += 1;
    });
    Object.values(dateFilters).forEach((range) => {
      if (range && (range.from || range.to)) c += 1;
    });
    return c;
  }, [columnFilters, dateFilters]);

  // Enhanced columns with filter buttons
  const safeColumns = useMemo(() => {
    return (columns || []).map((col) => {
      const base = { ...col };
      const field = String(base.field || "");
      const isSystemActionCol = field.startsWith("__") && field !== "__slno";

      if (base && typeof base === "object") {
        if (!base.headerName) {
          base.headerName = field
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase());
        }

        // Custom renderHeader with Filter icon
        if (!isSystemActionCol) {
          const isFilterActive =
            Boolean(columnFilters[field]?.length) ||
            Boolean(dateFilters[field]?.from || dateFilters[field]?.to);

          base.renderHeader = (params) => {
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  gap: 0.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12.5,
                    fontWeight: 800,
                    color: isFilterActive ? "#4F46E5" : "#0f172a",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                  }}
                >
                  {params.colDef.headerName}
                </Typography>

                <Tooltip title={`Filter by ${params.colDef.headerName}`}>
                  <IconButton
                    size="small"
                    onClick={(e) => handleOpenFilter(e, params.colDef)}
                    sx={{
                      p: 0.35,
                      color: isFilterActive ? "#4F46E5" : "#94a3b8",
                      bgcolor: isFilterActive ? "rgba(79, 70, 229, 0.12)" : "transparent",
                      border: isFilterActive ? "1px solid rgba(79, 70, 229, 0.3)" : "none",
                      borderRadius: 1.5,
                      "&:hover": {
                        bgcolor: "rgba(79, 70, 229, 0.15)",
                        color: "#4F46E5",
                      },
                    }}
                  >
                    <FilterListRoundedIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          };
        }

        // Default valueGetter robust to MUI X signatures
        if (!base.valueGetter) {
          if (field.startsWith("__")) {
            base.valueGetter = () => "";
          } else {
            base.valueGetter = (...args) => {
              try {
                if (args.length >= 2 && args[1] && typeof args[1] === "object" && !("row" in (args[0] || {}))) {
                  const [, row] = args;
                  return getFieldValue(row, base.field);
                }
                const params = args[0] || {};
                const row = params?.row || {};
                return getFieldValue(row, base.field);
              } catch {
                return args && args.length ? args[0] : "";
              }
            };
          }
        }

        // Default renderCell ensures text renders even if theme colors conflict
        if (!base.renderCell) {
          base.renderCell = (params) => {
            const row = params?.row || {};
            const v = getFieldValue(row, base.field);
            if (v == null || v === "") return "";
            return String(v);
          };
        }

        // Default valueFormatter
        if (!base.valueFormatter) {
          base.valueFormatter = (...args) => {
            try {
              const v = args && args.length ? args[0] : undefined;
              if (v == null) return "";
              return typeof v === "object" ? (v?.username || v?.name || v?.id || JSON.stringify(v)) : String(v);
            } catch {
              return "";
            }
          };
        }

        // Ensure reasonable width if not specified
        if (base.flex == null && base.width == null && base.minWidth == null) {
          base.minWidth = 145;
          base.flex = 1;
        }
      }
      return base;
    });
  }, [columns, columnFilters, dateFilters, getFieldValue]);

  const handlePaginationChange = useCallback((model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) return prev;
      return model;
    });
  }, []);

  const handleSortChange = useCallback((m) => {
    setSortModel(Array.isArray(m) ? m : []);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const totalPages = Math.max(1, Math.ceil(rowCount / paginationModel.pageSize));
  const currentPage = Math.min(paginationModel.page + 1, totalPages);
  const startEntry = rowCount === 0 ? 0 : paginationModel.page * paginationModel.pageSize + 1;
  const endEntry = Math.min((paginationModel.page + 1) * paginationModel.pageSize, rowCount);

  return (
    <div
      className="tk-card tk-grid"
      style={{
        width: "100%",
        background: "#ffffff",
        borderRadius: 12,
        border: "1px solid #e5e7eb",
        overflowX: "auto",
        overflowY: "hidden",
        position: "relative",
        isolation: "isolate",
      }}
    >
      {/* ── TOOLBAR & SEARCH ── */}
      <div style={{ padding: "10px 14px", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        {toolbar}
        <input
          placeholder="Search records..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{
            padding: "8px 12px",
            width: "min(280px, 100%)",
            borderRadius: 8,
            border: "1px solid #e5e7eb",
            backgroundColor: "#ffffff",
            fontSize: 13,
            outline: "none",
          }}
        />
      </div>

      {/* ── ACTIVE FILTERS CHIP BAR ── */}
      {activeFiltersCount > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            px: 2,
            py: 1,
            bgcolor: "#f1f5f9",
            borderTop: "1px solid #e2e8f0",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#475569" }}>
            Active Filters ({activeFiltersCount}):
          </Typography>

          {Object.entries(columnFilters).map(([field, vals]) => {
            if (!vals || vals.length === 0) return null;
            const col = (columns || []).find((c) => c.field === field);
            const title = col?.headerName || field;
            return (
              <Chip
                key={field}
                size="small"
                label={`${title}: ${vals.slice(0, 2).join(", ")}${vals.length > 2 ? ` (+${vals.length - 2})` : ""}`}
                onDelete={() => handleClearColFilter(field)}
                sx={{
                  bgcolor: "#e0e7ff",
                  color: "#3730a3",
                  fontWeight: 700,
                  fontSize: 11.5,
                  borderRadius: 2,
                }}
              />
            );
          })}

          {Object.entries(dateFilters).map(([field, range]) => {
            if (!range || (!range.from && !range.to)) return null;
            const col = (columns || []).find((c) => c.field === field);
            const title = col?.headerName || field;
            return (
              <Chip
                key={field}
                size="small"
                label={`${title}: ${range.from || "Start"} to ${range.to || "Now"}`}
                onDelete={() => handleClearColFilter(field)}
                sx={{
                  bgcolor: "#fef3c7",
                  color: "#92400e",
                  fontWeight: 700,
                  fontSize: 11.5,
                  borderRadius: 2,
                }}
              />
            );
          })}

          <Button
            size="small"
            onClick={handleClearAllFilters}
            sx={{
              fontSize: 11.5,
              fontWeight: 800,
              textTransform: "none",
              color: "#dc2626",
              p: 0.5,
            }}
          >
            Clear All
          </Button>
        </Box>
      )}

      {/* ── DATA GRID ── */}
      <DataGrid
        autoHeight
        rows={filteredRows}
        columns={safeColumns}
        rowCount={rowCount}
        loading={loading}
        paginationMode="server"
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortChange}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        onRowDoubleClick={(p) => onRowEdit?.(p.row)}
        disableRowSelectionOnClick
        density={density}
        checkboxSelection={checkboxSelection}
        hideFooterPagination={true}
        onRowSelectionModelChange={(m) => {
          setSelection(m);
          onSelectionChange?.(m);
        }}
        getRowId={(row) => row.id ?? row.pk ?? row.uuid ?? row._id ?? row.key}
        rowHeight={52}
        columnHeaderHeight={44}
        disableVirtualization
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        sx={{
          "& .MuiDataGrid-cell": {
            outline: "none !important",
            color: "#0f172a !important",
            backgroundColor: "transparent",
            borderRight: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
          },
          "& .MuiDataGrid-cell--textCenter": { justifyContent: "center" },
          "& .MuiDataGrid-cellContent": { color: "#0f172a !important" },
          "& .MuiDataGrid-columnHeaderTitle": { color: "#0f172a !important", fontWeight: 700 },
          "& .MuiDataGrid-columnHeaderTitleContainerContent": { color: "#0f172a !important" },
          "&.MuiDataGrid-root": { backgroundColor: "#ffffff", border: "none" },
          "& .MuiDataGrid-main": { backgroundColor: "#ffffff" },
          "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f8fafc", borderBottom: "1.5px solid #cbd5e1" },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: "#ffffff" },
          "& .MuiDataGrid-virtualScrollerContent": { backgroundColor: "#ffffff" },
          "& .MuiDataGrid-virtualScrollerRenderZone": { backgroundColor: "#ffffff" },
          "& .MuiDataGrid-row": { backgroundColor: "#ffffff" },
          "& .MuiDataGrid-footerContainer": { display: "none" },
          "& .MuiDataGrid-row:hover": { backgroundColor: "rgba(79, 70, 229, 0.04) !important" },
          "& .MuiDataGrid-row.Mui-selected": { backgroundColor: "rgba(79, 70, 229, 0.08) !important" },
          "& .MuiDataGrid-overlay": { backgroundColor: "#ffffff" },
          "& .MuiDataGrid-filler": { backgroundColor: "#ffffff" },
          "& .MuiDataGrid-row:nth-of-type(odd)": { backgroundColor: "#ffffff" },
          "& .MuiDataGrid-row:nth-of-type(even)": { backgroundColor: "#f8fafc" },
          "& .MuiDataGrid-row": { borderBottom: "1px solid #e2e8f0" },
          "& .MuiDataGrid-columnHeader": { borderRight: "1px solid #e2e8f0" },
          "& .MuiDataGrid-columnHeader:last-of-type": { borderRight: "none" },
          "& .MuiDataGrid-cell:last-of-type": { borderRight: "none" },
        }}
      />

      {/* ── COLUMN FILTER POPOVER (FACETED SELECTIONS WITH EXACT COUNTS) ── */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleCloseFilter}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            width: 320,
            maxHeight: 460,
            borderRadius: 3,
            boxShadow: "0 10px 40px rgba(0,0,0,0.16)",
            border: "1px solid #e2e8f0",
            p: 1.75,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {activeFilterCol && (
          <>
            {/* Popover Header */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>
                Filter: {activeFilterCol.headerName}
              </Typography>
              <IconButton size="small" onClick={handleCloseFilter}>
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* DATE FILTER MODE */}
            {isDateField(activeFilterCol.field, activeFilterCol.headerName) ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Typography sx={{ fontSize: 11.5, color: "#64748b", fontWeight: 700 }}>
                  Select Date Range:
                </Typography>

                <Stack spacing={1}>
                  <Box>
                    <Typography sx={{ fontSize: 11, color: "#475569", fontWeight: 700, mb: 0.5 }}>From Date</Typography>
                    <input
                      type="date"
                      value={dateFilters[activeFilterCol.field]?.from || ""}
                      onChange={(e) => handleDateFilterChange(activeFilterCol.field, "from", e.target.value)}
                      style={{
                        width: "100%",
                        padding: "7px 10px",
                        borderRadius: 8,
                        border: "1px solid #cbd5e1",
                        fontSize: 12.5,
                        outline: "none",
                      }}
                    />
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: 11, color: "#475569", fontWeight: 700, mb: 0.5 }}>To Date</Typography>
                    <input
                      type="date"
                      value={dateFilters[activeFilterCol.field]?.to || ""}
                      onChange={(e) => handleDateFilterChange(activeFilterCol.field, "to", e.target.value)}
                      style={{
                        width: "100%",
                        padding: "7px 10px",
                        borderRadius: 8,
                        border: "1px solid #cbd5e1",
                        fontSize: 12.5,
                        outline: "none",
                      }}
                    />
                  </Box>
                </Stack>

                <Typography sx={{ fontSize: 11, color: "#64748b", fontWeight: 700, mt: 0.5 }}>
                  Quick Presets:
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {[
                    { id: "TODAY", label: "Today" },
                    { id: "YESTERDAY", label: "Yesterday" },
                    { id: "LAST_7", label: "7 Days" },
                    { id: "LAST_30", label: "30 Days" },
                    { id: "THIS_MONTH", label: "This Month" },
                    { id: "ALL", label: "All Dates" },
                  ].map((p) => (
                    <Chip
                      key={p.id}
                      size="small"
                      label={p.label}
                      onClick={() => handleDatePreset(activeFilterCol.field, p.id)}
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: "pointer",
                        borderRadius: 1.5,
                      }}
                    />
                  ))}
                </Stack>

                <Divider sx={{ my: 1 }} />

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Button
                    size="small"
                    onClick={() => handleClearColFilter(activeFilterCol.field)}
                    sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, color: "#dc2626" }}
                  >
                    Reset Filter
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleCloseFilter}
                    sx={{ textTransform: "none", fontSize: 12, fontWeight: 800, bgcolor: "#4F46E5" }}
                  >
                    Done
                  </Button>
                </Box>
              </Box>
            ) : (
              /* CATEGORICAL / VALUES FILTER MODE WITH EXACT RECORD COUNTS */
              <>
                {/* Search in options */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 2,
                    border: "1px solid #cbd5e1",
                    mb: 1.25,
                    bgcolor: "#f8fafc",
                  }}
                >
                  <SearchRoundedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                  <input
                    placeholder="Search options..."
                    value={filterSearchQuery}
                    onChange={(e) => setFilterSearchQuery(e.target.value)}
                    style={{
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      fontSize: 12.5,
                      width: "100%",
                    }}
                  />
                  {filterSearchQuery && (
                    <IconButton size="small" onClick={() => setFilterSearchQuery("")} sx={{ p: 0.25 }}>
                      <CloseRoundedIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </Box>

                {/* Select All / Clear Row */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography sx={{ fontSize: 11, color: "#64748b", fontWeight: 700 }}>
                    {colDistinctOptions.length} available options
                  </Typography>
                  <Box>
                    <Button
                      size="small"
                      onClick={() => handleSelectAllOptions(activeFilterCol.field)}
                      sx={{ textTransform: "none", fontSize: 11, fontWeight: 700, p: 0.25, minWidth: 0, mr: 1 }}
                    >
                      Select All
                    </Button>
                    <Button
                      size="small"
                      onClick={() => handleClearColFilter(activeFilterCol.field)}
                      sx={{ textTransform: "none", fontSize: 11, fontWeight: 700, p: 0.25, minWidth: 0, color: "#dc2626" }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>

                <Divider sx={{ mb: 1 }} />

                {/* Option list with checkboxes & counts */}
                <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5, maxHeight: 220 }}>
                  {colDistinctOptions.length === 0 ? (
                    <Typography sx={{ fontSize: 12, color: "#94a3b8", textAlign: "center", py: 2 }}>
                      No matching options
                    </Typography>
                  ) : (
                    colDistinctOptions.map((opt) => {
                      const isChecked = (columnFilters[activeFilterCol.field] || []).includes(opt.value);
                      return (
                        <Box
                          key={opt.value}
                          onClick={() => handleToggleOption(activeFilterCol.field, opt.value)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 1,
                            py: 0.6,
                            borderRadius: 1.5,
                            cursor: "pointer",
                            "&:hover": { bgcolor: "#f1f5f9" },
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, overflow: "hidden", mr: 1 }}>
                            <Checkbox
                              size="small"
                              checked={isChecked}
                              icon={<CheckBoxOutlineBlankRoundedIcon sx={{ fontSize: 17, color: "#94a3b8" }} />}
                              checkedIcon={<CheckBoxRoundedIcon sx={{ fontSize: 17, color: "#4F46E5" }} />}
                              sx={{ p: 0 }}
                            />
                            <Typography
                              sx={{
                                fontSize: 12.5,
                                color: isChecked ? "#4F46E5" : "#1e293b",
                                fontWeight: isChecked ? 800 : 500,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {opt.value}
                            </Typography>
                          </Box>

                          <Chip
                            size="small"
                            label={opt.count}
                            sx={{
                              height: 20,
                              fontSize: 10.5,
                              fontWeight: 800,
                              bgcolor: isChecked ? "#4F46E5" : "#e2e8f0",
                              color: isChecked ? "#ffffff" : "#475569",
                              borderRadius: 1,
                            }}
                          />
                        </Box>
                      );
                    })
                  )}
                </Box>

                <Divider sx={{ my: 1 }} />

                {/* Popover footer */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography sx={{ fontSize: 11.5, color: "#64748b", fontWeight: 700 }}>
                    Selected: {(columnFilters[activeFilterCol.field] || []).length}
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleCloseFilter}
                    sx={{ textTransform: "none", fontSize: 12, fontWeight: 800, bgcolor: "#4F46E5" }}
                  >
                    Apply Filter
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </Popover>

      {/* ── NUMBERED PAGINATION BAR (Enterprise Standard with Page Buttons) ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderTop: "1px solid #e2e8f0",
          bgcolor: "#ffffff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 12.5, color: "#64748b", fontWeight: 700 }}>
            Rows per page:
          </Typography>
          <MuiSelect
            size="small"
            value={paginationModel.pageSize}
            onChange={(e) => {
              const newSize = Number(e.target.value);
              setPaginationModel({ page: 0, pageSize: newSize });
            }}
            sx={{
              height: 32,
              fontSize: 12.5,
              fontWeight: 800,
              borderRadius: "8px",
              bgcolor: "#f8fafc",
              "& .MuiSelect-select": { py: 0.5, px: 1.25 },
            }}
          >
            {[10, 25, 50, 100].map((size) => (
              <MenuItem key={size} value={size} sx={{ fontSize: 12.5, fontWeight: 700 }}>
                {size}
              </MenuItem>
            ))}
          </MuiSelect>
          <Typography sx={{ fontSize: 12.5, color: "#475569", fontWeight: 700, ml: 1 }}>
            {activeFiltersCount > 0 ? (
              <>
                Showing {filteredRows.length} filtered records (of {rowCount.toLocaleString("en-IN")})
              </>
            ) : (
              <>
                Showing {startEntry}–{endEntry} of {rowCount.toLocaleString("en-IN")} records
              </>
            )}
          </Typography>
        </Box>

        <Pagination
          color="primary"
          shape="rounded"
          variant="outlined"
          page={currentPage}
          count={totalPages}
          onChange={(_, val) => {
            setPaginationModel((prev) => ({ ...prev, page: val - 1 }));
          }}
          showFirstButton
          showLastButton
          siblingCount={1}
          boundaryCount={1}
          renderItem={(item) => (
            <PaginationItem
              {...item}
              sx={{
                fontWeight: 800,
                fontSize: 12.5,
                minWidth: 32,
                height: 32,
                borderRadius: "8px",
                "&.Mui-selected": {
                  bgcolor: "#4F46E5 !important",
                  color: "#ffffff",
                  borderColor: "#4F46E5",
                },
              }}
            />
          )}
        />
      </Box>
    </div>
  );
}
